import React, { useEffect, useRef, useState } from 'react';
import {AppBar, Avatar, Box, Button, Container, IconButton, Link, Menu, MenuItem, Toolbar, Tooltip, Typography } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../redux/hooks';
import { setIsAuth0Authenticated, setRegistrant, setJwtToken, setShowLoginMessage } from '../../../redux/reducers/registrantSlice';
import { setUserProfile } from '../../../redux/reducers/userProfileSlice';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import './headerMenuBar.css';
import { UserRecord } from '../../../models/UserRecord';
import { UserRecordApiRsp } from '../../../models/UserRecordApiRsp';
import _ from 'lodash';

export default function HeaderMenuBar() {

  const { firstName, lastName, email, isAuth } = useAppSelector((state: any) => state.registrant);
  const { showMinimalHeaderInfo } = useAppSelector((state: any) => state.eventConfig);
  const { isEventAdmin, ownerAddress } = useAppSelector((state: any) => state.userProfile);
  const dispatch = useAppDispatch();

  const [ anchorElNav, setAnchorElNav ] = useState<null | HTMLElement>(null);
  const [ anchorElUser, setAnchorElUser ] = useState<null | HTMLElement>(null);
  const [ anchorElUserNotLoggedIn, setAnchorElUserNotLoggedIn ] = useState<null | HTMLElement>(null);
  const { loginWithPopup, logout, user, isAuthenticated, getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const isFetchingUserRecord = useRef(false);

  useEffect(() => {
    if (_.get(ownerAddress, 'first', '') !== '') {
      dispatch(setRegistrant({
        firstName: ownerAddress.first,
        lastName: ownerAddress.last
      }));
    }
    if (_.get(user, 'name', '') !== '') {
      const myEmail = email !== '' ? email : _.get(user, 'email', '').trim();
      const newAdministerEventId = window.location.pathname === '/configurations' ? undefined :  window.location.pathname === '/' ? '/home' : window.location.pathname
      dispatch(setRegistrant({
        email: myEmail,
        isAdmin: true,
        isAuth: isAuthenticated,
        isAuth0Authenticated: isAuthenticated,
        administerEventId: newAdministerEventId
      }));
    }
  });

  useEffect(() => {
    async function fetchData() {
      let token: string|undefined = '';
      const getTokenOptions = {
        authorizationParams: {
          audience: 'https://events/api',
          scope: 'read:profile',
        }
      }
      try {
        token = await getAccessTokenSilently(getTokenOptions);
      } catch(e: any) {
        if (e.error === 'consent_required') {
          token = await getAccessTokenWithPopup(getTokenOptions);
        }
      }
      dispatch(setJwtToken(token ? token : ''));

      try {
        if (!isFetchingUserRecord.current) {
          isFetchingUserRecord.current = true;
          const userRsp: UserRecordApiRsp = await axios.get(`/api/protected/user/${ _.get(user, 'email', '')}`, {
            headers: { 
              Authorization: `Bearer ${token}`,
            }
          });
          if (_.get(userRsp, 'data.userProfileStatus', 0) === 404) {
            const userRecord: UserRecord = {
              _id: _.get(user, 'email',''), 
              userProfileStatus: 0,
              createdDate: new Date().toISOString(),
              updatedDate: new Date().toISOString(),
              events: [],
              dogs: [],
              isEventAdmin: false,
              ownerAddress: {
                first: '',
                last: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                phone: '',
                email: _.get(user, 'email','')
              }
            }
            await axios.post(`/api/protected/user/${userRecord._id}`, userRecord, { headers: { Authorization: `Bearer ${token}`}});
            dispatch(setUserProfile({
              _id: userRecord._id,
              userProfileStatus: 200,
              events: userRecord.events,
              ownerAddress: userRecord.ownerAddress,
              dogs: userRecord.dogs,
              isEventAdmin: userRecord.isEventAdmin
            }));  
          } else {
            dispatch(setUserProfile({
              _id: _.get(userRsp, 'data._id', ''),
              userProfileStatus: _.get(userRsp, 'data.userProfileStatus', 0),
              events: _.get(userRsp, 'data.events', []),
              ownerAddress: _.get(userRsp, 'data.ownerAddress', null),
              dogs: _.get(userRsp, 'data.dogs', []),
              isEventAdmin: _.get(userRsp, 'data.isEventAdmin', false)
            }));  
          }
        }
      } catch(err: any)  {
        console.log('DEBUG: user get/post error=' + JSON.stringify(err, null, 2));
      }
    }
    if (isAuthenticated && user) {
      const myEmail = email !== '' ? email : _.get(user, 'email', '').trim();
      dispatch(setRegistrant({
        email: myEmail,
        isAuth: isAuthenticated,
        isAuth0Authenticated: isAuthenticated
      }));
      fetchData();
    }
  }, [user, isAuthenticated, dispatch, getAccessTokenSilently, email, getAccessTokenWithPopup]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenUserMenuNotLoggedIn = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserNotLoggedIn(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleCloseUserMenuNotLoggedIn = () => {
    setAnchorElUserNotLoggedIn(null);
  };

  const updateAu0Authenticated = (dispatch: any, val: boolean) => {
    return new Promise<void>((resolve) => {
      dispatch(setIsAuth0Authenticated(val));
      resolve();
    })
  }

  const handleOnClickUserLogout = () => {
    updateAu0Authenticated(dispatch, false).then(() => {
      logout({ logoutParams: { returnTo: window.location.origin } });
      dispatch(setRegistrant({
        firstName: '',
        lastName: '',
        email: '',
        isAuth: false,
        isAuthenticated: false,
        isAdmin: false,
        allowCookies: false,
        jwtToken: ''
      }));
    });
    setAnchorElUser(null);
  }

  const handleLogin = async (event: any) => {
    event.preventDefault();
    // handleCloseUserMenu();
    handleCloseUserMenuNotLoggedIn();
    try {
      await loginWithPopup(); 
     } catch(e) {
      console.log('login cancelled/closed')
    }
  }

  const handleSetShowLoginMessage = async (event: any) => {
    dispatch(setShowLoginMessage(true));
    handleCloseUserMenuNotLoggedIn();
  }

  return (
    <AppBar position="sticky" color="primary" className="noprint">
      <Container maxWidth="xl"  className="app-header-menu-bar">
        <Toolbar disableGutters>
          <Link style={{textTransform: 'none'}} href="/" underline="none">
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ mr: 2, color: 'white', display: { xs: 'none', md: 'flex' } }}
            >
              Tracy Fletcher Event Services
            </Typography>
          </Link>
          { !showMinimalHeaderInfo ?
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              <Link
                  sx={{ my: 2, color: 'white', display: 'block' }}
                  style={{textTransform: 'none', fontSize: '1em' }}
                  href="/products"
                  underline="none"
                >
                  Products
              </Link>
            </Box>
            : null
          }
          
          { isAuth && isEventAdmin ?
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              <Link
                  id="header-menu-bar-configuration"
                  sx={{ my: 2, color: 'white', display: 'block' }}
                  style={{textTransform: 'none', fontSize: '1em'}}
                  href="/configurations"
                  underline="none"
                >
                  Configurations
              </Link>
            </Box>
            : null
          }
          { !showMinimalHeaderInfo || window.location.pathname ==='/configurations' ?
            <Box sx={{ flexGrow: 0 }}>
              { isAuth ?
                <>
                  <Tooltip title="Open settings">
                    <Button
                      key='login-button'
                      onClick={handleOpenUserMenu}
                      style={{textTransform: 'none', fontSize: '1em'}}
                      sx={{ my: 2, color: 'white', display: 'block' }}
                    >
                      { firstName === '' ? email : `${firstName} ${lastName}` }
                    </Button>
                  </Tooltip>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    style={{textTransform: 'none'}}
                    anchorEl={anchorElUser}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                  >
                    <Link href="/userProfile" style={{ textDecoration: 'none' }} underline='none'>
                      <MenuItem onClick={handleCloseUserMenu}>
                        <Typography textAlign="center">My Profile</Typography>
                      </MenuItem>
                    </Link>
                    
                    <MenuItem onClick={() => handleOnClickUserLogout()}>
                      <Typography textAlign="center">Log Out</Typography>
                    </MenuItem>
                  </Menu>
                </>
                : 
                <>
                  <IconButton 
                    color="primary" 
                    aria-label="user"
                    component="span"
                    onClick={handleOpenUserMenuNotLoggedIn}>
                    <Avatar sx={{ bgcolor: '#cc9966' }}></Avatar>
                  </IconButton>
                  <Menu
                    sx={{ mt: '45px' }}
                    id="menu-appbar"
                    style={{textTransform: 'none'}}
                    anchorEl={anchorElUserNotLoggedIn}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorElUserNotLoggedIn)}
                    onClose={handleCloseUserMenuNotLoggedIn}
                  >
                    <MenuItem onClick={(e: any) => handleSetShowLoginMessage(e)}>
                      <Typography textAlign="center">Why Log In?</Typography>
                    </MenuItem>
                    
                    <MenuItem onClick={(e: any) => handleLogin(e)}>
                      <Typography textAlign="center">Log In</Typography>
                    </MenuItem>
                  </Menu>
                </>
              }
            </Box>
            : null
          }
        </Toolbar>
      </Container>
    </AppBar>
  )
}
